/**
 * SW-148: helpers compartidos por OpportunitiesPage + OpportunityDetailPage.
 *
 * Antes vivían inline en cada archivo — `hashIndex` estaba duplicado con
 * formulaciones ligeramente distintas (>>> 0 vs | 0). Esta extracción DRY
 * + tests unitarios garantizan comportamiento estable cross-page.
 *
 * Todas las funciones son PURAS — no tocan DOM, fetch, ni react state.
 * Test cobertura completa en `_helpers.test.ts`.
 */

/**
 * Separa un nombre "Cliente — Título" en sus dos partes. Soporta em-dash
 * (—), en-dash (–) y hyphen (-) como separador, rodeados de whitespace.
 *
 * Ejemplos:
 *   "Acme Corp — Senior Backend" → { clientLabel: "Acme Corp", titleLabel: "Senior Backend" }
 *   "Sin separador"               → { clientLabel: null, titleLabel: "Sin separador" }
 */
export function parseShortlistName(name: string): {
  clientLabel: string | null;
  titleLabel: string;
} {
  const splitMatch = name.match(/^(.+?)\s+[—–-]\s+(.+)$/);
  if (splitMatch && splitMatch[1] && splitMatch[2]) {
    return { clientLabel: splitMatch[1].trim(), titleLabel: splitMatch[2].trim() };
  }
  return { clientLabel: null, titleLabel: name };
}

/**
 * Hash determinístico simple para mapear un input string → variant index
 * estable. Suficiente para distribuir avatares por color (NO es crypto-grade
 * ni se usa para nada sensible). Para el mismo input + modulo, siempre devuelve
 * el mismo índice.
 *
 * Implementación canónica: multiplicación por 31 (clásica de Java hashCode) +
 * `>>> 0` para forzar conversion a unsigned int32 — garantiza positivo sin
 * Math.abs (que falla para Number.MIN_SAFE_INTEGER, irrelevante acá pero
 * pattern más limpio).
 */
export function hashIndex(input: string, modulo: number): number {
  if (modulo <= 0) return 0;
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash * 31 + input.charCodeAt(i)) >>> 0;
  }
  return hash % modulo;
}

/**
 * String locale-aware tipo "today", "2 days ago", "Mar 5". Usa built-in
 * `Intl.RelativeTimeFormat` cuando la diferencia ≤ 7 días, y
 * `Intl.DateTimeFormat` (month + day) para fechas más antiguas.
 *
 * Si `iso` no parsea, devuelve el string tal cual (defensa contra payloads
 * malformados — el FE NO debe crashear).
 */
export function formatRelativeDate(iso: string, locale: string): string {
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    const diffMs = Date.now() - date.getTime();
    const diffDays = Math.floor(diffMs / 86_400_000);

    if (diffDays < 1) {
      return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(0, 'day');
    }
    if (diffDays <= 7) {
      return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(-diffDays, 'day');
    }
    return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(date);
  } catch {
    return iso;
  }
}

/**
 * Iniciales para mostrar como fallback de avatar.
 *   "Ada Lovelace"  → "AL"
 *   "Cher"          → "CH"
 *   ""              → "·"
 *
 * Tomamos la primera letra de las primeras 2 palabras (split por whitespace).
 * Si hay 1 sola palabra, los 2 primeros chars de esa. Vacío → glyph neutro.
 */
export function computeInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '·';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

/**
 * Concatena city + country con coma, ignorando nulls/strings vacíos.
 * Devuelve `null` si ambos son ausentes (el caller decide qué renderizar).
 *
 *   ("London", "UK")        → "London, UK"
 *   (null,     "UK")        → "UK"
 *   ("",       "UK")        → "UK"
 *   (null,     null)        → null
 */
export function formatLocation(city: string | null, country: string | null): string | null {
  const parts = [city, country].filter((p): p is string => !!p && p.trim().length > 0);
  return parts.length > 0 ? parts.join(', ') : null;
}
